import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  NAVIGATION_HEADER_HEIGHT,
  NavigationHeader,
} from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { doPlansLoadPlan } from 'store/plan/actions';
import {
  selectAfterActionFind,
  selectPlansPins,
  selectPlansFindNoun,
  selectPlansPlanId,
  selectPlansEditable,
  selectPlansWithFloorOrder,
} from 'store/plan/selectors';

import { useEsc } from 'hooks/useEsc';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import { Platform, View } from 'react-native';
import { TextField } from 'components/Field/TextField';
import { ViewWithSeparators } from 'components/ViewWithSeparators';

type Props = ModalScreenProps<ModalScreens.PLANLIST>;

export const PlanListModal: React.FC<Props> = () => {
  const dispatch = useDispatch();

  const [searchValues, setSearchValue] = React.useState<string[]>([]);
  const findNoun = useSelector(selectPlansFindNoun);
  const afterActionFind = useSelector(selectAfterActionFind);
  const isFind = !!afterActionFind;
  const plansEditable = useSelector(selectPlansEditable);
  const allowCreate = !isFind && plansEditable;

  const planPinCount = useSelector(selectPlansPins).reduce<
    Record<string, number>
  >((acc, pin) => {
    acc[pin.planId] = (acc[pin.planId] || 0) + 1;
    return acc;
  }, {});

  const plans = Object.values(useSelector(selectPlansWithFloorOrder));
  const filteredPlans = plans
    .filter(filterArrayEmpty)
    .filter(
      (plan) =>
        (!isFind || !!planPinCount[plan.id]) &&
        (!searchValues.length ||
          searchValues.some((searchValue) =>
            plan.name.toLowerCase().includes(searchValue),
          )),
    )
    .sort((a, b) => a.floorOrder - b.floorOrder);

  const planId = useSelector(selectPlansPlanId);

  const handleCreatePlan = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.PLANCREATE,
        params: {},
      }),
    );

  const handleLoadPlan = React.useCallback(
    (id: string) => {
      dispatch(NAVIGATE_MODAL_BACK());
      setTimeout(() => {
        dispatch(doPlansLoadPlan({ id }));
      }, 0);
    },
    [dispatch],
  );

  const handleSearchChange = (newSearchValue: string) =>
    setSearchValue(
      newSearchValue.toLowerCase().split(' ').filter(filterArrayEmpty),
    );
  const handleClose = () => dispatch(NAVIGATE_MODAL_BACK());

  useEsc(handleClose);

  return (
    <PageScrollView
      isInModal={Platform.OS !== 'ios' || !!filteredPlans.length}
      header={
        <ViewWithSeparators>
          <NavigationHeader
            heading="Plans"
            saveButtonText="New"
            onPressSaveButton={allowCreate ? handleCreatePlan : undefined}
            onPressCancelButton={handleClose}
            cancelButtonText="Close"
          />
          {!!filteredPlans.length && (
            <View
              style={{
                minHeight: NAVIGATION_HEADER_HEIGHT,
                paddingHorizontal: theme.paddingHorizontal,
                justifyContent: 'center',
              }}
            >
              <TextField
                placeholder="Search Plans"
                value=""
                iconLeft={ICONS.MagnifyingGlassLight}
                onChange={handleSearchChange}
                clearable
                onChangeDebounceMs={300}
                containerStyle={{
                  backgroundColor: '#ffffff',
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                  overflow: 'hidden',
                }}
                inputStyle={{ minHeight: null }}
                disabled={false}
                autoCorrect={false}
                autoComplete="off"
                autoCapitalize="none"
                onEsc={handleClose}
              />
            </View>
          )}
        </ViewWithSeparators>
      }
      contentContainerStyle={
        filteredPlans.length
          ? undefined
          : {
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
            }
      }
      emptyChild={
        <PageEmpty
          icon={ICONS.MapLocationLight}
          text={!allowCreate ? 'There are no plans' : undefined}
          button={
            allowCreate
              ? { onPress: handleCreatePlan, text: 'Create a Plan' }
              : undefined
          }
        />
      }
    >
      {!!filteredPlans.length && (
        <Card
          explanation={
            isFind ? `Showing plans with ${findNoun} pins` : undefined
          }
        >
          {filteredPlans.map((plan) => (
            <CardRow
              key={plan.id}
              label={plan.name}
              label2={
                isFind
                  ? pluralise(
                      planPinCount[plan.id] || 0,
                      '1 Pin',
                      '{x} Pins',
                      'No Pins',
                    )
                  : undefined
              }
              radioValue={plan.id === planId}
              onPress={() => handleLoadPlan(plan.id)}
              contentLeft={
                <LocalThumbnail
                  thumbnailId={
                    plan.fileId + (plan.filePage ? `:${plan.filePage}` : '')
                  }
                  width={70}
                  height={70}
                  style={{
                    borderColor: '#DDDDDD',
                    borderRadius: 4,
                    borderWidth: 1,
                    marginRight: theme.paddingHorizontal * 2,
                    marginVertical: theme.paddingHorizontal,
                  }}
                  contentFit="contain"
                />
              }
            />
          ))}
        </Card>
      )}
    </PageScrollView>
  );
};
